// BCM · Goods Receipt Dashboard — Sync PO / Sync Inventory hook.
//
// Lifts the payload builders from sasl/batch/updateBatch/ViewBatchPageButtons.jsx
// (do not rewrite them) and drives the sync contract from doc rev V4/V5:
//   * Sync PO   -> A14b updateFGBatchForWorkflowReset (responds on `status`)
//     — same endpoint as resubmitDocuments / the sasl/batchesDetails
//     Phase-2-reset flow (payload is an array of
//     buildPOWorkflowResetPayload(source), isStorageOrAddressOrKeyChange
//     always true).
//   * Sync Inv  -> A13 syncInventoryDataNew  (responds on `success`).
//   * Both syncs follow the SAME 2-call chain when resetsBatch is true: the
//     sync above, THEN A16 resetWorkflowFromScratch (batchResetFor:
//     "PO Sync" / "Inventory Sync"). Both calls must succeed for the overall
//     action to be a success. When resetsBatch is false, only step 1 runs.
//
// Aligned with the shared GRFeedbackContext (Sep 2026): success and failure
// flow through feedback.notifySuccess / feedback.notifyError, so Sync PO / Sync
// Inventory now share the exact same on-screen behaviour as every other batch-
// mutating action — green toast top-right (4s) + non-blocking "Updating Batch"
// toast bottom-right while the batch reset workflow settles (only for the
// resetsBatch path; the plain sync isn't status-changing).
//
// Options:
//   onRefresh — parent's dashboard refetch. Required when any caller sets
//               resetsBatch=true, so the status-change poll can drive it.
//
// Return shape (public API — callers still destructure this):
//   { running, runSync, buildPOWorkflowResetPayload, buildSyncInventoryPayload }
// The previous `toast`, `resultDialog`, `closeToast`, `closeResultDialog` fields
// were removed with SyncProgressToast — the shared feedback context owns the
// visuals now.
import { useCallback, useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  syncInventory,
  resetWorkflowFromScratch,
  updateFGBatchForWorkflowReset,
  updateSASLDSDataNew,
} from "../api/goodsReceiptApi";
import { useGRFeedback } from "../context/GRFeedbackContext";
import { GENERIC_ERROR } from "../utils/constants";

// Body for the resetWorkflowFromScratch call — poNumber/sponsorBatchNumber/
// batchNumber use the SAME row fields as the rest of this module's payloads.
const buildResetWorkflowPayload = (source, batchResetFor) => ({
  poNumber: (source && source.scheduleAgreementNumber) || null,
  sponsorBatchNumber: (source && source.vendorBatchNumber) || null,
  batchNumber: (source && source.vendorBatchNumber) || null,
  vendorMosymphonyID: (source && source.vendorMosymphonyID) || null,
  schrockenDeliveryScheduleBatchID: (source && source.schrockenDeliveryScheduleBatchID) || null,
  batchResetFor,
});

const toISODate = (d) => new Date(d).toISOString().slice(0, 10); // YYYY-MM-DD
const startOfDay = (d) => `${toISODate(d)}T00:00:00Z`;
const endOfDay = (d) => `${toISODate(d)}T23:59:59Z`;

// Sync PO now goes through updateFGBatchForWorkflowReset (A14b) — the SAME
// request shape as the sasl/batchesDetails Phase-2-reset caller and
// ResubmitDocumentDialog's resubmitDocuments call (both hit this endpoint as
// an array of these objects). isStorageOrAddressOrKeyChange is always true
// for Sync PO (unconditional — unlike the Phase-2-reset flow, where it
// depends on the reset reason picked in that dialog).
export const buildPOWorkflowResetPayload = (source) => ({
  schrockenDeliveryScheduleBatchID: (source && source.schrockenDeliveryScheduleBatchID) || "",
  poNumber: (source && source.scheduleAgreementNumber) || "",
  batchNumber: (source && source.vendorBatchNumber) || "",
  vendorMosymphonyID: (source && source.vendorMosymphonyID) || "",
  creationDate: (source && source.creationDate) || "",
  vendorNumber: (source && source.vendorNumber) || "",
  vendorPlant: (source && source.vendorPlant) || "",
  isStorageOrAddressOrKeyChange: true,
});

// Sync PO payload for a batch at SASLBatchStatusID 50 (Post-Shipment Documents
// Uploaded) — matches PurchaseOrderTabTable.jsx's handleSyncPO exactly (same
// dateRanges-from-agreementDate/creationDate shape, same 4 fields). Used ONLY
// for status 50, inside runSync below; every other status keeps building
// buildPOWorkflowResetPayload above, unchanged.
export const buildSASLDSDataPayload = (source) => {
  const startSrc = (source && (source.agreementDate || source.creationDate)) || null;
  const startDate = startSrc ? `${String(startSrc).slice(0, 10)}T00:00:00Z` : endOfDay(new Date());
  return {
    dateRanges: [{ startDate, endDate: endOfDay(new Date()) }],
    vendorNumber: source && source.vendorNumber ? [source.vendorNumber] : [],
    vendorPlant: source && source.vendorPlant ? [source.vendorPlant] : [],
    poNumber: source && source.scheduleAgreementNumber ? [source.scheduleAgreementNumber] : [],
  };
};

export const buildSyncInventoryPayload = (source) => {
  const today = new Date();
  const past = new Date(today);
  past.setFullYear(past.getFullYear() - 1);
  const materialNumbers = [];
  const comps =
    (source && (source.bomComponentDetails || source.BomConsumptionDetails)) ||
    [];
  comps.forEach((c) => {
    if (c && c.BomComponentCode) materialNumbers.push(c.BomComponentCode);
  });
  return {
    dateRanges: [{ startDate: startOfDay(past), endDate: endOfDay(today) }],
    vendorNumber: source && source.vendorNumber ? [source.vendorNumber] : [],
    vendorPlant: source && source.vendorPlant ? [source.vendorPlant] : [],
    materialNumber: materialNumbers,
    inventoryBatchNumber: [], // always empty — sync stays at material level
    snapshot: "S",
    zeroStockLines: "N",
  };
};

export default function useSyncWithRecheck({ onRefresh } = {}) {
  const feedback = useGRFeedback();
  // Tracks WHICH sync is in flight ('PO' | 'INVENTORY' | null) — not just a
  // shared boolean — so Sync PO and Sync Inventory can each show their own
  // loading state instead of both lighting up whenever either one runs.
  const [runningType, setRunningType] = useState(null);
  const mountedRef = useRef(true);
  const cancelRef = useRef(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cancelRef.current) cancelRef.current.cancel("leaving GR dashboard");
    };
  }, []);

  // Only surface a server-provided message (e.g. business-rule failure).
  // Axios's own err.message for a bare HTTP failure (e.g. "Request failed
  // with status code 404") is not user-facing — fall back to the generic
  // toast copy instead.
  const errorText = (err) =>
    (err.response && err.response.data && (err.response.data.message || err.response.data.error)) ||
    GENERIC_ERROR;

  // type: 'PO' | 'INVENTORY'; payload built by the caller for the sync call;
  // source = row merged with detail, used to build the reset call's payload;
  // resetsBatch per V5 — only then is the reset treated as status-changing
  // (Updating Batch toast + poll) instead of a plain success toast.
  // onSyncSuccess (optional) — fired once the sync itself has succeeded,
  // regardless of the reset path. PA3 uses this to re-read the allocation so
  // the allocated-batch validation re-runs against the freshly synced
  // inventory.
  const runSync = useCallback(
    ({ type, payload, resetsBatch, source, onSyncSuccess }) => {
      const actionLabel = type === "PO" ? "Sync PO" : "Sync Inventory";
      setRunningType(type);
      const cancelSource = axios.CancelToken.source();
      cancelRef.current = cancelSource;

      // Snapshot the row's current status BEFORE we mutate — the Updating Batch
      // toast's poll compares against this.
      const prevStatus = source && source.SASLBatchStatusID;
      const batchId = source && source.schrockenDeliveryScheduleBatchID;

      const done = () => {
        if (mountedRef.current) setRunningType(null);
      };

      const afterSuccess = (statusChanging, successMessage) => {
        if (typeof onSyncSuccess === "function") {
          try { onSyncSuccess(type); } catch (e) { /* non-fatal */ }
        }
        feedback.notifySuccess(
          statusChanging
            ? {
                message: successMessage,
                statusChanging: true,
                // Only the resetsBatch path is status-changing — it restarts the workflow.
                resetsBatch: true,
                batchId,
                prevStatus,
                refetch: onRefresh,
                actionLabel,
              }
            : { message: successMessage, statusChanging: false }
        );
        done();
      };

      // Sync PO — step 1: updateFGBatchForWorkflowReset (A14b). When
      // resetsBatch is true, step 2 runs the SAME resetWorkflowFromScratch
      // (A16) call as Sync Inventory below (batchResetFor: "PO Sync"), so
      // both syncs follow the identical 2-step chain/toast behaviour. Body is
      // an array — see buildPOWorkflowResetPayload. Responds on `status`.
      //
      // BCM: a batch at SASLBatchStatusID 50 (Post-Shipment Documents
      // Uploaded) syncs PO via updateSASLDSDataNew instead — the SAME
      // endpoint/payload PurchaseOrderTabTable.jsx's handleSyncPO uses, which
      // responds on `success`, not `status`. Every other status keeps the
      // existing updateFGBatchForWorkflowReset call, completely unchanged —
      // only step 1's call and its success-flag check differ for status 50;
      // the resetsBatch / step-2 / error handling below is shared as-is.
      if (type === "PO") {
        const isPostShipmentUploaded = source && source.SASLBatchStatusID === 50;
        const step1 = isPostShipmentUploaded
          ? updateSASLDSDataNew(buildSASLDSDataPayload(source), cancelSource.token)
          : updateFGBatchForWorkflowReset([payload], cancelSource.token);
        return step1
          .then((res) => {
            if (!mountedRef.current) return res.data;
            const ok = isPostShipmentUploaded
              ? !!(res.data && res.data.success)
              : !!(res.data && res.data.status);
            if (!ok) {
              feedback.notifyError(
                (res.data && res.data.message) ||
                  "An error occurred while syncing PO data."
              );
              done();
              return res.data;
            }
            // If no reset, this is not status-changing — a simple green toast
            // is enough, no Updating Batch poll needed.
            if (!resetsBatch) {
              afterSuccess(false, "Purchase Order data has been synced from SAP successfully.");
              return res.data;
            }
            // Step 2 — same as Sync Inventory: kick off the real workflow
            // reset (A16). It responds fast, then the Temporal workflow
            // actually settles the batch async — that window is what the
            // Updating Batch toast covers.
            const resetPayload = buildResetWorkflowPayload(source, "PO Sync");
            return resetWorkflowFromScratch(
              source && source.vendorName,
              resetPayload,
              cancelSource.token
            )
              .then((res2) => {
                if (!mountedRef.current) return res.data;
                const ok2 = !!(res2.data && res2.data.status);
                if (!ok2) {
                  feedback.notifyError(
                    (res2.data && res2.data.message) || "Batch reset failed after sync."
                  );
                  done();
                  return res.data;
                }
                afterSuccess(true, "PO synced and batch reset triggered. Refreshing the batch…");
                return res.data;
              })
              .catch((err2) => {
                if (axios.isCancel(err2)) { done(); return null; }
                feedback.notifyError(errorText(err2));
                done();
                throw err2;
              });
          })
          .catch((err) => {
            if (axios.isCancel(err)) { done(); return null; }
            if (!mountedRef.current) { done(); return null; }
            feedback.notifyError(errorText(err));
            done();
            throw err;
          });
      }

      // Sync Inventory — unchanged 2-step chain: syncInventory (A13, responds
      // on `success`) then, when resetsBatch, resetWorkflowFromScratch (A16).
      return syncInventory(payload, cancelSource.token)
        .then((res) => {
          if (!mountedRef.current) return res.data;
          const ok = !!(res.data && res.data.success);
          if (!ok) {
            
            feedback.notifyError(
              (res.data && res.data.message) ||
                "An error occurred while syncing inventory data."
            );
            done();
            return res.data;
          }
          // If no reset, this is not status-changing — a simple green toast is
          // enough, no Updating Batch poll needed.
          if (!resetsBatch) {
            afterSuccess(false, "Inventory data has been synced from SAP successfully.");
            return res.data;
          }
          // Step 2 — kick off the real workflow reset (A16). It responds fast,
          // then the Temporal workflow actually settles the batch async — that
          // window is what the Updating Batch toast covers.
          const resetPayload = buildResetWorkflowPayload(source, "Inventory Sync");
          return resetWorkflowFromScratch(
            source && source.vendorName,
            resetPayload,
            cancelSource.token
          )
            .then((res2) => {
              if (!mountedRef.current) return res.data;
              const ok2 = !!(res2.data && res2.data.status);
              if (!ok2) {
                feedback.notifyError(
                  (res2.data && res2.data.message) || "Batch reset failed after sync."
                );
                done();
                return res.data;
              }
              // Reset accepted server-side. Hand off to the shared toast+poll —
              // green success top-right (4s), then Updating Batch bottom-right
              // until the batch's status flips (or 45s cap).
              afterSuccess(true, "Inventory synced and batch reset triggered. Refreshing the batch…");
              return res.data;
            })
            .catch((err2) => {
              if (axios.isCancel(err2)) { done(); return null; }
              feedback.notifyError(errorText(err2));
              done();
              throw err2;
            });
        })
        .catch((err) => {
          if (axios.isCancel(err)) { done(); return null; }
          if (!mountedRef.current) { done(); return null; }
          feedback.notifyError(errorText(err));
          done();
          throw err;
        });
    },
    [feedback, onRefresh]
  );

  return {
    // Back-compat: true while EITHER sync is in flight (unused internally,
    // kept in case another caller still reads it as a blanket busy flag).
    running: runningType !== null,
    // Per-button state — callers should prefer this to avoid lighting up
    // both Sync PO and Sync Inventory whenever only one of them is running.
    runningType,
    runSync,
    buildPOWorkflowResetPayload,
    buildSyncInventoryPayload,
  };
}
